import type { FC } from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import type { IDocumentItemProps, IDocumentsInfo2, IDocumentsInfoStoreData } from '../../interfaces';
import { TranslationKey } from '../../models';
import { DocumentItem } from '../DocumentItem';
import {
  StyledDivBoxExpandHistory,
  StyledDivExpander,
  StyledDivHistory,
  StyledExpanedIcon,
  StyledList,
} from './DocumentStyledComponents';

interface IDocumentHistoryProps {
  temporaryDocument: boolean;
  historyExpanded: boolean;
  documentIds?: number[];
  documentTypeId: number;
  deleteConfirmationRequired: boolean;
  editableDocument?: boolean;
  histroyDocumentsInfoData?: IDocumentsInfo2[];
  removeDocument?: (documentId: number) => void;
  handleToggleClick: () => void;
  documentPending: (document: IDocumentsInfoStoreData) => boolean;
  mapToDocumentItemProps: (
    documentStoreData: IDocumentsInfoStoreData,
    canDelete: boolean,
    isShowApprovalState: boolean,
    deleteConfirmationRequired: boolean,
    removeDocument?: (documentId: number) => void,
  ) => IDocumentItemProps;
}

const renderDocumentItem = (documentProps: IDocumentItemProps, key: number) => (
  <DocumentItem
    key={key}
    {...documentProps}
  />
);

export const DocumentHistory: FC<IDocumentHistoryProps> = (props) => {
  const {
    temporaryDocument,
    historyExpanded,
    documentIds,
    documentTypeId,
    deleteConfirmationRequired,
    histroyDocumentsInfoData,
    handleToggleClick,
    documentPending,
    mapToDocumentItemProps,
    removeDocument,
  } = props;

  const getInfoByType = (typeId?: number, includeTemporary?: boolean) =>
    histroyDocumentsInfoData
      ?.filter((info) => {
        const isTemporary = includeTemporary ?? false;

        return info.documentTypeId === typeId && (!info.isTemporary || isTemporary);
      })
      .sort((a, b) => {
        if (a.createdDate.getTime() && b.createdDate.getTime()) {
          return a.createdDate.getTime() - b.createdDate.getTime();
        }

        return 0;
      }) as IDocumentsInfoStoreData[];

  const getHistoryDocuments = (): IDocumentItemProps[] => {
    if (!documentIds?.length) {
      return [];
    }

    const documentItems: IDocumentItemProps[] = getInfoByType(documentTypeId, temporaryDocument)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- we need this condition
      ?.filter((document) => documentIds.includes(document.documentId) && !documentPending(document))
      .map((document) => mapToDocumentItemProps(document, false, true, deleteConfirmationRequired, removeDocument));

    return documentItems;
  };

  const renderHistory = () => {
    const documents = getHistoryDocuments();
    //  eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- is not alway truthy we need this condition
    const existingDocuments = documents && documents.length > 0;
    if (!existingDocuments) {
      return null;
    }

    return (
      <StyledDivHistory>
        <StyledDivExpander style={{ display: historyExpanded ? 'block' : 'none' }}>
          <StyledList>{documents.map(renderDocumentItem)}</StyledList>
        </StyledDivExpander>
        <StyledDivBoxExpandHistory onClick={() => handleToggleClick()}>
          <Localization
            id={TranslationKey.history}
            defaultMessage={defaultTranslations.hide}
          />
          <StyledExpanedIcon style={{ transform: historyExpanded ? 'rotate(180deg)' : 'none' }} />
        </StyledDivBoxExpandHistory>
      </StyledDivHistory>
    );
  };

  const documentsHistory = renderHistory();

  return <StyledDivHistory>{documentsHistory}</StyledDivHistory>;
};
